import React from 'react';
import './CapabilityCategoryModal.css';
import apiService from '../services/api';

function CapabilityCategoryModal({ 
  isOpen, 
  onClose, 
  category, 
  onSave, 
  isSaving = false,
  selectedRole,
  availableRoles = [],
  selectedDepartment,
  accessRole
}) {
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [weightage, setWeightage] = React.useState('');
  const [role, setRole] = React.useState('');
  const [rolesList, setRolesList] = React.useState([]);
  const [loadingRoles, setLoadingRoles] = React.useState(false);
  const [error, setError] = React.useState('');

  // SINGLE, CORRECT useEffect — fetches roles for Admin
  React.useEffect(() => {
    if (!isOpen || accessRole !== 'admin' || !selectedDepartment) {
      setRolesList([]);
      setRole('');
      return;
    }

    const fetchRoles = async () => {
      setLoadingRoles(true);
      try {
        const dept = window.availableDepartments?.find(d => d.name === selectedDepartment);
        if (!dept?.id) {
          console.warn('Department ID not found for:', selectedDepartment);
          setRolesList([]);
          setLoadingRoles(false);
          return;
        }

        const endpoint = `/metadata?type=roles&department_id=${dept.id}`;
        console.log('Fetching roles →', endpoint);

        const response = await apiService.fetchData(endpoint);
        console.log('API Response:', response);

        let rolesArray = [];

        if (response?.status === 'success' && Array.isArray(response.data)) {
          rolesArray = response.data;
        }

        const formattedRoles = rolesArray
          .filter(item => item.RoleName)  // Your API uses "RoleName" (capital R)
          .map(item => ({
            roleName: item.RoleName.trim()
          }));

        console.log('Roles loaded into dropdown:', formattedRoles);

        setRolesList(formattedRoles);

        // Auto-select first role when adding new category
        if (!category && formattedRoles.length > 0) {
          setRole(formattedRoles[0].roleName);
        }
      } catch (err) {
        console.error('Failed to fetch roles:', err);
        setRolesList([]);
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, [isOpen, selectedDepartment, accessRole, category]);

  // Reset form when modal opens/closes or category changes
  React.useEffect(() => {
    if (!isOpen) {
      setName('');
      setDescription('');
      setWeightage('');
      setRole('');
      setError('');
      return;
    }

    if (category) {
      setName(category.name || '');
      setDescription(category.description || '');
      setWeightage(category.percentage?.replace('%', '') || '');
      setRole(category.role_name || selectedRole || '');
    } else {
      setName('');
      setDescription('');
      setWeightage('');
      setRole('');
    }
    setError('');
  }, [isOpen, category, selectedRole]);

  const handleSubmit = () => {
    setError('');

    if (!name.trim() || !description.trim() || !weightage) {
      setError('All fields are required');
      return;
    }

    const numWeightage = parseInt(weightage);
    if (isNaN(numWeightage) || numWeightage < 1 || numWeightage > 100) {
      setError('Weightage must be between 1 and 100');
      return;
    }

    if (accessRole === 'admin' && !role) {
      setError('Please select a role');
      return;
    }

    onSave({
      id: category?.id,
      name: name.trim(),
      description: description.trim(),
      percentage: numWeightage,
      role_name: accessRole === 'admin' ? role : selectedRole
    });
  };

  if (!isOpen) return null;

  const isAdmin = accessRole === 'admin';

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h3>{category ? 'Edit Category' : 'Add Category'}</h3>
          <button onClick={onClose} className="modal-close-btn">×</button>
        </div>

        <div className="modal-body">

          {/* ROLE FIELD */}
          <div className="form-group">
            <label>ROLE {isAdmin && <span style={{color: 'red'}}>*</span>}</label>
            {isAdmin ? (
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={loadingRoles}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  backgroundColor: loadingRoles ? '#f3f4f6' : 'white',
                  fontSize: '14px'
                }}
              >
                <option value="">
                  {loadingRoles 
                    ? 'Loading roles...' 
                    : rolesList.length === 0 
                      ? 'No roles found' 
                      : '-- Select Role --'
                  }
                </option>
                {rolesList.map((r, i) => (
                  <option key={i} value={r.roleName}>
                    {r.roleName}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={role || selectedRole || 'N/A'}
                disabled
                style={{ backgroundColor: '#f3f4f6', color: '#6b7280', padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb' }}
              />
            )}
          </div>
          
          {isAdmin && (
            <div className="form-group">
              <label>DEPARTMENT</label>
              <input
                type="text"
                value={selectedDepartment || 'Not selected'}
                disabled
                style={{
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontWeight: '500',
                  cursor: 'not-allowed'
                }}
              />
              <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Department is fixed based on your selection above
              </small>
            </div>
          )}
          
          <div className="form-group">
            <label>CATEGORY NAME 
              <span className="ca-required">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength="50"
              placeholder="e.g. Technical Expertise"
            />
          </div>

          <div className="form-group">
            <label>WEIGHTAGE 
              <span className="ca-required">*</span>
            </label>
            <div className="weightage-input">
              <input
                type="number"
                min="1"
                max="100"
                value={weightage}
                onChange={(e) => setWeightage(e.target.value)}
                placeholder="25"
              />
              <span>%</span>
            </div>
          </div>

          <div className="form-group">
            <label>DESCRIPTION 

              <span className="ca-required">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="4"
              maxLength="200"
              placeholder="Describe about category..."
            />
          </div>

          {error && (
            <div className="error-message" style={{
              backgroundColor: '#fee2e2',
              color: '#991b1b',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #fecaca',
              fontSize: '14px',
              marginTop: '10px'
            }}>
              {error}
            </div>
          )}

        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-cancel" disabled={isSaving}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={isSaving} className="btn-save">
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CapabilityCategoryModal;